<template>
  <WidgetLayout>
    <div class="flex justify-center align-center text-center">
      <div
        :class="
          generalStore.iFrame
            ? 'w-96 min-h-[680px] bg-white p-4 flex'
            : 'w-96 min-h-[680px] bg-white p-4 shadow-md rounded flex'
        "
      >
        <div v-if="isLoading" class="h-content m-auto">
          <LayupSpinner />
          <p class="text-base-black text-lg my-3">Please wait while we process your payment.</p>

          <div class="text-grey-light-900 text-sm mt-2" v-if="isLoadingTakingSomeTime">
            <p class="font-bold text-base-black text-lg my-3">Payment pending</p>
            <p class="text-grey-light-900 text-sm">
              Your payment is taking unexpectedly long to process. Please wait or go to our
              <a class="text-brand-500 p-0" :href="dashboardLink" target="_blank"> dashboard</a> to track your payment
              status.
            </p>
          </div>
        </div>
        <div class="h-content m-auto" v-else>
          <div v-if="isPaymentSuccessful">
            <SuccessIcon />
            <p class="font-bold text-base-black text-lg my-3">Payment confirmed</p>
            <p class="text-grey-light-900 text-sm">
              You will be redirected shortly. Click
              <a v-if="!generalStore.iFrame" class="text-brand-500 p-0" :href="link">here</a
              ><a v-if="generalStore.iFrame" class="text-brand-500 p-0" href="" @click="closeIframe">here</a> to be
              redirected now.
            </p>
          </div>
          <div v-else>
            <FailureIcon />
            <p class="font-bold text-base-black text-lg my-3">Payment unsuccessful</p>
            <p class="text-grey-light-900 mb-3 text-sm">Please try again.</p>
            <button
              class="border rounded w-100 relative h-10 text-base-white hover:bg-gray-200 mt-2 p-2"
              @click="router.back()"
            >
              <div class="flex flex-row justify-center">
                <img src="@/assets/icons/arrow-left.svg" class="size-5" /><span class="text-base-black">Go back</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  </WidgetLayout>
</template>
<script setup lang="ts">
import LayupSpinner from '@/components/common/LayupSpinner.vue';
import FailureIcon from '@/components/icons/FailureIcon.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';
import { BASE_URL } from '@/config';
import { NetworkError } from '@/errors';
import WidgetLayout from '@/layouts/WidgetLayout.vue';
import { getPaymentPlan } from '@/services/paymentPlans';
import { getPayment } from '@/services/payments';
import { useGeneralStore } from '@/stores/general';
import { useWidgetStore } from '@/stores/widget';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { getRedirectUrl } from '@/utils/order-widget.utils';
import { delay } from '@/utils/time.utils';
import { onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toast-notification';

const route = useRoute();
const router = useRouter();

const generalStore = useGeneralStore();
const widgetStore = useWidgetStore();
const isPaymentSuccessful = ref(false);
const isLoading = ref(false);
const isLoadingTakingSomeTime = ref(false);
const toast = useToast();
const orderId = ref('');
const link = ref('');
const dashboardLink = ref(`${BASE_URL}/shopper`);

onMounted(async () => {
  await verifyPayment();
});

const waitForPaymentStatus = async (id: string) => {
  let payment = await getPayment(id);
  while (payment?.pending!) {
    payment = await getPayment(id);
    await delay(10000);
  }
  return payment;
};

const verifyPayment = async () => {
  isLoading.value = true;
  let paymentProvider = 'unknown';
  let paymentMethod = 'unknown';

  setTimeout(() => {
    isLoadingTakingSomeTime.value = true;
  }, 10000);
  try {
    const id = route.params.id || route.query.paymentId;
    if (id) {
      const payment = await waitForPaymentStatus(id as string);
      paymentProvider = payment.connectorData?.paymentProvider?.toLowerCase() ?? 'unknown';
      paymentMethod = payment.connectorData?.paymentMethod?.toLowerCase() ?? 'unknown';
      const paymentPlan = await getPaymentPlan(payment.destinationId);
      orderId.value = paymentPlan.orderId ? paymentPlan.orderId : '';
      link.value = getRedirectUrl(orderId.value, payment);

      if (payment?.paid) {
        addMetric('accept_payment', {
          status: 'success',
          payment_provider: paymentProvider,
          payment_method: paymentMethod,
          channel: 'widget',
        });
        widgetStore.successfulPayment = payment;
        isPaymentSuccessful.value = true;
        setTimeout(() => {
          window.parent.postMessage({ type: 'layup-close', status: 'success', orderId: orderId.value }, '*');
        }, 5000);
        setTimeout(() => {
          widgetStore.reset();
          try {
            if (generalStore.iFrame) {
              window.parent.location.href = link.value;
            } else {
              window.location.href = link.value;
            }
          } catch (error) {
            createWarning(error);
          }
        }, 10000);
      } else {
        if (generalStore.iFrame) {
          window.parent.postMessage({ type: 'layup-close', status: 'failed', orderId: orderId.value }, '*');
        }
        addMetric('accept_payment', {
          status: 'failed',
          payment_provider: paymentProvider,
          payment_method: paymentMethod,
          channel: 'widget',
        });
      }
    }
  } catch (error: any) {
    let errorMessage = '';
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric('accept_payment', {
        status: 'network_error',
        payment_provider: paymentProvider,
        payment_method: paymentMethod,
        channel: 'widget',
      });
      errorMessage = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric('accept_payment', {
        status: 'error',
        payment_provider: paymentProvider,
        payment_method: paymentMethod,
        channel: 'widget',
      });
      errorMessage = 'An unexpected error occurred. Error verifying payment';
      if (error?.response?.data) {
        errorMessage = error.response.data;
      }
    }

    toast.open({
      message: errorMessage,
      type: 'error',
      position: 'top',
    });
  } finally {
    isLoading.value = false;
  }
};

const closeIframe = () => {
  window.parent.postMessage({ type: 'layup-close', status: 'success', orderId: orderId.value }, '*');
  try {
    if (generalStore.iFrame) {
      setTimeout(() => {
        window.parent.location.href = link.value;
      }, 1000);
    } else {
      window.location.href = link.value;
    }
  } catch (error) {
    createWarning(error);
  }
};
</script>
